import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 15000;      // 15 s (user chose 'b')

/**
 * LaunchChatWidget — floating "Ask my Growth Team" chat pinned on the
 * launch status portal. Reads/writes public `/api/launch/chat/{token}`.
 */
export default function LaunchChatWidget({ publicToken, businessName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);
  const lastAtRef = useRef(null);

  const load = async () => {
    try {
      const params = lastAtRef.current ? { since: lastAtRef.current } : {};
      const r = await axios.get(`${API}/launch/chat/${publicToken}`, { params });
      const incoming = r.data.items || [];
      if (incoming.length === 0 && lastAtRef.current) return;
      // Merge / dedupe by id
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const merged = [...prev];
        for (const m of incoming) if (!seen.has(m.id)) merged.push(m);
        merged.sort((a, b) => (a.at > b.at ? 1 : -1));
        if (merged.length) lastAtRef.current = merged[merged.length - 1].at;
        return merged;
      });
      setLoaded(true);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!publicToken) return;
    load();
    timerRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicToken]);

  useEffect(() => {
    if (open && scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/launch/chat/${publicToken}`,
        { text: text.trim() });
      const msg = r.data?.message;
      if (msg) {
        setMessages((prev) => [...prev, msg]);
        lastAtRef.current = msg.at;
      }
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message failed to send");
    } finally { setSending(false); }
  };

  const applicantMessageCount = messages.filter((m) => m.sender !== "applicant").length;
  const unreadTeamCount = open ? 0 : applicantMessageCount;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-2xl px-5 py-3 font-semibold text-sm"
        data-testid="launch-chat-open"
      >
        <MessageCircle className="w-4 h-4" /> Ask my Growth Team
        {unreadTeamCount > 0 && (
          <span className="ml-1 min-w-[20px] text-center rounded-full bg-orange-500 text-[11px] font-bold px-1.5 py-0.5">
            {unreadTeamCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-sm h-[540px] max-h-[80vh] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 flex flex-col"
         data-testid="launch-chat-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-900 to-orange-900 text-white rounded-t-2xl">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Live chat</div>
          <div className="text-sm font-bold">Growth Team · WebsiteTech</div>
        </div>
        <button onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/10 text-white/80"
                data-testid="launch-chat-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50"
            data-testid="launch-chat-messages">
        {!loaded && (
          <div className="text-center text-slate-400 pt-8">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-1" /> Loading…
          </div>
        )}
        {loaded && messages.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-8 px-4">
            Say hi 👋 — we usually reply within a few hours during business hours.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id}
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  m.sender === "applicant"
                    ? "ml-auto bg-orange-500 text-white rounded-br-none"
                    : "bg-white ring-1 ring-slate-200 text-slate-800 rounded-bl-none"}`}
                data-testid={`launch-chat-msg-${m.sender}`}>
            <div className="whitespace-pre-line">{m.text}</div>
            <div className={`text-[10px] mt-1 ${m.sender === "applicant" ? "text-orange-100" : "text-slate-400"}`}>
              {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-100 p-2 flex items-end gap-2">
        <textarea value={text}
                   onChange={(e) => setText(e.target.value)}
                   onKeyDown={(e) => {
                     if (e.key === "Enter" && !e.shiftKey) {
                       e.preventDefault(); send();
                     }
                   }}
                   rows={1}
                   placeholder={`Ask about ${businessName || "your launch"}…`}
                   className="flex-1 resize-none text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none max-h-32"
                   data-testid="launch-chat-input" />
        <button onClick={send}
                disabled={sending || !text.trim()}
                className="p-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40"
                data-testid="launch-chat-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
